import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import isEmpty from 'lodash.isempty';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import {HiOutlineCheck} from '@react-icons/all-files/hi/HiOutlineCheck';
import {HiOutlineX} from '@react-icons/all-files/hi/HiOutlineX';
import {CgRadioCheck} from '@react-icons/all-files/cg/CgRadioCheck';
import {CgRadioChecked} from '@react-icons/all-files/cg/CgRadioChecked';
import classnames from 'classnames';
import InfoButton from '@components/shared/InfoButton/InfoButton';
import {ZENDESK_ARTICLES} from '@components/shared/InfoArticle/infoArticleConstants';
import VSFeature, {Disabled, Enabled} from '@components/shared/VSFeature/VSFeature';
import {VS_FEATURES} from '@constants/index';
import InfoLink from '@components/shared/InfoLink/InfoLink';
import {Card, CardHeader, CardBody} from '@components/shared/Card';
import Loading from '../../shared/Loading/Loading';
import PasswordForm from '../../shared/PasswordForm/PasswordForm';
import {
    fetchPasswordPolicy,
    savePassword,
    selectIsPasswordPolicyFetching,
    selectIsPasswordSaving,
    selectPasswordPolicy,
    selectPasswordSaveError
} from './passwordSlice';
import useErrorNotification from '../../../hooks/useErrorNotification';
import useNotifications from '../../../hooks/useNotifications';
import PageTitle from '../../shared/PageTitle';
import './changePassword.less';

export default function ChangePassword({
    onChange = (_event) => {},
    setIsValidPassword,
    isRegistration,
    isResetPassword = false,
    updatePassword = undefined,
    passwordPolicy,
    registration
}) {
    const intl = useIntl();
    const {showSuccessAlert} = useNotifications();
    const dispatch = useDispatch();
    const policy = useSelector(selectPasswordPolicy);
    const {
        minimumPasswordLength,
        shouldIncludeUppercase,
        shouldIncludeLowercase,
        shouldIncludeNumbers,
        shouldIncludeSpecialCharacters,
        minimumRulesRequired
    } = useMemo(() => {
        const source = passwordPolicy || policy || {};
        return {
            minimumPasswordLength: parseInt(source.minimum_password_length, 10) || 8,
            shouldIncludeUppercase: source.require_one_upper_character === 'true',
            shouldIncludeLowercase: source.require_one_lower_character === 'true',
            shouldIncludeNumbers: source.require_one_number_character === 'true',
            shouldIncludeSpecialCharacters: source.require_one_special_character === 'true',
            minimumRulesRequired: parseInt(source.amount_minimum_rules_requirements, 10) || 0
        };
    }, [passwordPolicy, policy]);
    const isPasswordPolicyFetchings = useSelector(selectIsPasswordPolicyFetching);
    const isPasswordSaving = useSelector(selectIsPasswordSaving);
    const passwordSaveError = useSelector(selectPasswordSaveError);
    const [wasConfirmedPasswordChanged, setWasConfirmedPasswordChanged] = useState(false);
    const [form, setForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmedPassword: ''
    });

    useErrorNotification(passwordSaveError);

    function renderCheckIcon(value) {
        return value
            ? <HiOutlineCheck className="check-icon success" />
            : <HiOutlineX className="check-icon failed" />;
    }

    function renderSubCheckIcon(value) {
        return value
            ? <CgRadioChecked className="sub-check-icon success" />
            : <CgRadioCheck className="sub-check-icon failed" />;
    }

    function changeForm({target}) {
        setForm({
            ...form,
            [target.name]: target.value
        });
        onChange({target});

        if (target.name === 'confirmedPassword' && !wasConfirmedPasswordChanged) {
            setWasConfirmedPasswordChanged(true);
        }
    }

    function getHasMinimumCharacters() {
        return minimumPasswordLength > 0 && form.newPassword.length >= minimumPasswordLength;
    }

    function getHasUppercaseCharacters() {
        const regExp = /[A-Z]/;
        return shouldIncludeUppercase && regExp.test(form.newPassword);
    }

    function getHasLowercaseCharacters() {
        const regExp = /[a-z]/;
        return shouldIncludeLowercase && regExp.test(form.newPassword);
    }

    function getHasNumbers() {
        const regExp = /[0-9]/;
        return shouldIncludeNumbers && regExp.test(form.newPassword);
    }

    function getHasSpecialCharacters() {
        const regExp = /[~ ! @ # $ % ^ & *]/;
        return shouldIncludeSpecialCharacters && regExp.test(form.newPassword);
    }

    const hasMinimumCharacters = getHasMinimumCharacters();
    const hasUppercaseCharacters = getHasUppercaseCharacters();
    const hasLowercaseCharacters = getHasLowercaseCharacters();
    const hasNumbers = getHasNumbers();
    const hasSpecialCharacters = getHasSpecialCharacters();

    function getHasAllRequiredRules() {
        const passedChecks = [
            hasUppercaseCharacters,
            hasLowercaseCharacters,
            hasNumbers,
            hasSpecialCharacters
        ].filter((isRulePassed) => isRulePassed);

        return passedChecks.length >= minimumRulesRequired;
    }

    const hasAllRequiredRules = getHasAllRequiredRules();
    const isConfirmedPasswordWrong = form.newPassword !== form.confirmedPassword;
    const shouldAllowUpdatePassword = !isConfirmedPasswordWrong && hasMinimumCharacters && hasAllRequiredRules;
    const shouldDisableSave = isResetPassword ? !shouldAllowUpdatePassword
        : (form.currentPassword === '' || isConfirmedPasswordWrong || !hasMinimumCharacters || !hasAllRequiredRules);
    const isEmbedded = isResetPassword || isRegistration;
    const containerClassName = classnames('account-change-password change-password', {
        'reset-password-page': isResetPassword,
        'is-embedded': isEmbedded
    });

    async function save() {
        if (isResetPassword) {
            updatePassword(form, shouldAllowUpdatePassword);
        } else {
            const wasSuccessful = await dispatch(savePassword({
                newPassword: form.newPassword,
                password: isRegistration ? form.confirmedPassword : form.currentPassword

            }));
            if (wasSuccessful) {
                showSuccessAlert(intl.formatMessage({id: 'esl.feedback.password_updated.success', defaultMessage: ''}));
            }
        }
    }

    useEffect(() => {
        if (isEmpty(passwordPolicy)) {
            dispatch(fetchPasswordPolicy());
        }
    }, []);

    useEffect(() => {
        if (isRegistration) {
            setIsValidPassword(hasAllRequiredRules && !isConfirmedPasswordWrong);
        }
    }, [isRegistration, hasAllRequiredRules, isConfirmedPasswordWrong]);

    const titleNode = (
        <span className="vs-card-title-with-info">
            {intl.formatMessage({id: 'page.account.changePassword', defaultMessage: ''})}
            <VSFeature feature={VS_FEATURES.IN_APP_ARTICLES}>
                <Enabled>
                    <InfoButton articleId={ZENDESK_ARTICLES.CHANGE_PASSWORD} />
                </Enabled>
                <Disabled>
                    <InfoLink infoKey="change_password" />
                </Disabled>
            </VSFeature>
        </span>
    );

    const headerAction = (
        <AppButton
            onClick={save}
            disabled={shouldDisableSave}
            className="save-btn"
        >
            {intl.formatMessage({id: 'esl.generic.submit', defaultMessage: ''})}
        </AppButton>
    );

    const strengthRules = (
        <div className="strength-checklist" aria-live="polite">
            <div className="strength-checklist__title">
                {intl.formatMessage({id: 'page.changePassword.passwordStrength', defaultMessage: ''})}
            </div>
            <ul className="strength-checklist__list">
                <li className="strength-checklist__item">
                    <span className="strength-checklist__icon">{renderCheckIcon(hasMinimumCharacters)}</span>
                    <span className="strength-checklist__label">
                        {intl.formatMessage(
                            {id: 'page.changePassword.passwordLength', defaultMessage: ''},
                            {length: minimumPasswordLength}
                        )}
                    </span>
                </li>
                {!!minimumRulesRequired && (
                    <li className="strength-checklist__item">
                        <span className="strength-checklist__icon">{renderCheckIcon(hasAllRequiredRules)}</span>
                        <span className="strength-checklist__label">
                            {intl.formatMessage(
                                {id: 'page.changePassword.requiredRules', defaultMessage: ''},
                                {requiredRules: minimumRulesRequired}
                            )}
                        </span>
                    </li>
                )}
                {(shouldIncludeUppercase || shouldIncludeLowercase || shouldIncludeNumbers || shouldIncludeSpecialCharacters) && (
                    <li className="strength-checklist__sublist">
                        <ul>
                            {shouldIncludeUppercase && (
                                <li className="strength-checklist__subitem">
                                    <span className="strength-checklist__icon">{renderSubCheckIcon(hasUppercaseCharacters)}</span>
                                    <span className="strength-checklist__label">
                                        {intl.formatMessage({id: 'page.changePassword.includeUppercase', defaultMessage: ''})}
                                    </span>
                                </li>
                            )}
                            {shouldIncludeLowercase && (
                                <li className="strength-checklist__subitem">
                                    <span className="strength-checklist__icon">{renderSubCheckIcon(hasLowercaseCharacters)}</span>
                                    <span className="strength-checklist__label">
                                        {intl.formatMessage({id: 'page.changePassword.includeLowercase', defaultMessage: ''})}
                                    </span>
                                </li>
                            )}
                            {shouldIncludeNumbers && (
                                <li className="strength-checklist__subitem">
                                    <span className="strength-checklist__icon">{renderSubCheckIcon(hasNumbers)}</span>
                                    <span className="strength-checklist__label">
                                        {intl.formatMessage({id: 'page.changePassword.includeNumbers', defaultMessage: ''})}
                                    </span>
                                </li>
                            )}
                            {shouldIncludeSpecialCharacters && (
                                <li className="strength-checklist__subitem">
                                    <span className="strength-checklist__icon">{renderSubCheckIcon(hasSpecialCharacters)}</span>
                                    <span className="strength-checklist__label">
                                        {intl.formatMessage({id: 'page.changePassword.includeSpecialCharacters', defaultMessage: ''})}
                                    </span>
                                </li>
                            )}
                        </ul>
                    </li>
                )}
            </ul>
        </div>
    );

    const body = (
        <>
            <Loading isLoading={isPasswordPolicyFetchings || isPasswordSaving} />
            {!isResetPassword && !isRegistration && (
                <div className="form-section">
                    <PasswordForm
                        value={form.currentPassword}
                        onChange={changeForm}
                        label={intl.formatMessage({id: 'esl.changepassword.current_password', defaultMessage: ''})}
                        isVisible
                        name="currentPassword"
                    />
                </div>
            )}
            <div className="form-section">
                <div className="form-grid">
                    <div className="form-grid__field new-password">
                        <PasswordForm
                            label={registration ? `${intl.formatMessage({id: 'esl.generic.password', defaultMessage: ''})} *`
                                : `${intl.formatMessage({id: 'esl.changepassword.new_password', defaultMessage: ''})}`}
                            placeholder={registration && '••••••••'}
                            value={form.newPassword}
                            onChange={changeForm}
                            isVisible
                            variant={registration ? 'standard' : 'outlined'}
                            name="newPassword"
                        />
                    </div>
                    <div className="form-grid__field confirm-password">
                        <PasswordForm
                            label={registration ? `${intl.formatMessage(
                                {id: 'esl.generic.confirm_password', defaultMessage: ''}
                            )} *`
                                : `${intl.formatMessage(
                                    {id: 'esl.changepassword.confirm', defaultMessage: ''}
                                )}`}
                            placeholder={registration && '••••••••'}
                            value={form.confirmedPassword}
                            onChange={changeForm}
                            isVisible
                            variant={registration ? 'standard' : 'outlined'}
                            name="confirmedPassword"
                            error={isConfirmedPasswordWrong && wasConfirmedPasswordChanged && intl.formatMessage({
                                id: 'esl.validation.password_match',
                                defaultMessage: ''
                            })}
                        />
                    </div>
                </div>
            </div>
            <div className="form-section form-section--strength">
                {strengthRules}
            </div>
            {!isRegistration && isEmbedded && (
                <div className="embedded-action">
                    <AppButton onClick={save} className="save-btn" disabled={shouldDisableSave}>
                        {intl.formatMessage({id: 'esl.generic.submit', defaultMessage: ''})}
                    </AppButton>
                </div>
            )}
        </>
    );

    if (isEmbedded) {
        return (
            <div className={containerClassName}>
                <PageTitle pageTitle={intl.formatMessage({id: 'page.account.changePassword', defaultMessage: ''})} />
                <div className="account-page-body">{body}</div>
            </div>
        );
    }

    return (
        <Card className={containerClassName} elevation={1} padding="md">
            <PageTitle pageTitle={intl.formatMessage({id: 'page.account.changePassword', defaultMessage: ''})} />
            <CardHeader
                title={titleNode}
                description={intl.formatMessage({id: 'esl.account.changePassword.description', defaultMessage: ''})}
                action={headerAction}
            />
            <CardBody>{body}</CardBody>
        </Card>
    );
}
